import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Check,
  ArrowRight,
  ArrowLeft,
  Shield,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "../config/api.config";
import logger from "../utils/logger";

const RegisterTenant = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: معلومات المكتب
    tenantName: "",
    subdomain: "",
    contactEmail: "",
    contactPhone: "",
    address: "",

    // Step 2: معلومات المدير
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    adminPasswordConfirm: "",

    // Step 3: الخطة
    planId: location.state?.selectedPlan || 1,
  });

  const [errors, setErrors] = useState({});
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [plans, setPlans] = useState([]);

  // جلب خطط الاشتراك
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/tenants/plans`);

      if (response.data.success && Array.isArray(response.data.data)) {
        const formattedPlans = response.data.data.map((plan) => {
          const features = Array.isArray(plan.features) ? plan.features : [];
          const limits = plan.limits_json || {};
          const finalFeatures = [];

          if (limits.cases) {
            finalFeatures.push(
              limits.cases === -1
                ? "عدد غير محدود من القضايا"
                : `إدارة حتى ${limits.cases} قضية`
            );
          }
          if (limits.users) {
            finalFeatures.push(
              limits.users === -1
                ? "عدد غير محدود من المستخدمين"
                : `${limits.users} مستخدم`
            );
          }
          if (limits.clients) {
            finalFeatures.push(
              limits.clients === -1
                ? "عدد غير محدود من العملاء"
                : `${limits.clients} عميل`
            );
          }
          if (limits.storage_gb) {
            finalFeatures.push(`مساحة تخزين ${limits.storage_gb} جيجابايت`);
          }

          features.forEach((feature) => {
            if (typeof feature === "string") {
              finalFeatures.push(feature);
            }
          });

          return {
            id: plan.id,
            name: plan.name_ar || plan.name,
            description: plan.description || "",
            price: parseFloat(plan.price) || 0,
            period: plan.billing_period === "monthly" ? "شهريًا" : "سنويًا",
            features: finalFeatures,
            isPopular:
              plan.name.toLowerCase() === "professional" || plan.id === 3,
            isContact: plan.name.toLowerCase() === "premium" || plan.id === 4,
          };
        });

        setPlans(formattedPlans);
      } else {
        setPlans([]);
      }
    } catch (error) {
      logger.error("Error fetching plans:", error);
      setPlans([]);
    }
  };

  // التحقق من توفر subdomain
  const checkSubdomainAvailability = async (value) => {
    if (!value || value.length < 3) {
      setSubdomainAvailable(null);
      return;
    }

    setCheckingSubdomain(true);
    try {
      const response = await axios.get(
        `${API_URL}/tenants/check-subdomain?subdomain=${value}`
      );
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      logger.error("Error checking subdomain:", error);
      // في حالة الخطأ، نعتبر النطاق متاح (للسماح بالمتابعة في حالة عدم تشغيل السيرفر)
      setSubdomainAvailable(true);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  // التحقق من صحة البيانات
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.tenantName) newErrors.tenantName = "يرجى إدخال اسم المكتب";
      if (!formData.subdomain) newErrors.subdomain = "يرجى إدخال النطاق الفرعي";
      else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
        newErrors.subdomain =
          "يجب أن يحتوي على حروف إنجليزية صغيرة وأرقام وشرطات فقط";
      }
      if (!formData.contactEmail)
        newErrors.contactEmail = "يرجى إدخال البريد الإلكتروني";
      else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
        newErrors.contactEmail = "صيغة البريد الإلكتروني غير صحيحة";
      }
      if (!formData.contactPhone)
        newErrors.contactPhone = "يرجى إدخال رقم الهاتف";
      else if (
        !/^(07[3-9]\d{8}|(\+?964)?7[3-9]\d{8})$/.test(
          formData.contactPhone.replace(/\s/g, "")
        )
      ) {
        newErrors.contactPhone =
          "يجب أن يبدأ بـ 07 ويتكون من 11 رقم (مثال: 07901234567)";
      }
      if (!formData.address) newErrors.address = "يرجى إدخال العنوان";
    }

    if (step === 1) {
      if (!formData.adminName) newErrors.adminName = "يرجى إدخال اسم المدير";
      if (!formData.adminEmail)
        newErrors.adminEmail = "يرجى إدخال البريد الإلكتروني";
      else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
        newErrors.adminEmail = "صيغة البريد الإلكتروني غير صحيحة";
      }
      if (!formData.adminPassword)
        newErrors.adminPassword = "يرجى إدخال كلمة المرور";
      else if (formData.adminPassword.length < 8) {
        newErrors.adminPassword = "يجب أن تكون كلمة المرور 8 أحرف على الأقل";
      }
      if (!formData.adminPasswordConfirm) {
        newErrors.adminPasswordConfirm = "يرجى تأكيد كلمة المرور";
      } else if (formData.adminPassword !== formData.adminPasswordConfirm) {
        newErrors.adminPasswordConfirm = "كلمات المرور غير متطابقة";
      }
    }

    if (step === 2) {
      if (!formData.planId) newErrors.planId = "يرجى اختيار خطة";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // التغيير في الحقول
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // إزالة الخطأ عند التعديل
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }

    // التحقق من subdomain
    if (field === "subdomain") {
      // السماح بالأحرف الإنجليزية والأرقام والشرطات فقط، وتحويل إلى أحرف صغيرة
      const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
      setFormData((prev) => ({ ...prev, subdomain: cleanValue }));

      // التحقق من التوفر فقط إذا كان الطول مناسب
      if (cleanValue.length >= 3) {
        checkSubdomainAvailability(cleanValue);
      } else {
        setSubdomainAvailable(null);
      }
      return; // العودة هنا لتجنب التكرار
    }
  };

  // الانتقال للمرحلة التالية
  const handleNext = () => {
    if (!validateStep(currentStep)) {
      return;
    }

    if (currentStep === 0 && !subdomainAvailable) {
      setErrors({ subdomain: "النطاق الفرعي غير متاح" });
      return;
    }

    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  };

  // الرجوع للمرحلة السابقة
  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  // إتمام التسجيل
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    try {
      const { adminPasswordConfirm, ...submitData } = formData;

      // التأكد من إرسال جميع البيانات
      const registrationData = {
        tenantName: submitData.tenantName,
        subdomain: submitData.subdomain,
        contactEmail: submitData.contactEmail,
        contactName: submitData.adminName,
        contactPhone: submitData.contactPhone,
        address: submitData.address,
        adminName: submitData.adminName,
        adminEmail: submitData.adminEmail,
        adminPassword: submitData.adminPassword,
        planId: submitData.planId,
      };

      logger.info("📤 إرسال بيانات التسجيل:", registrationData);

      const response = await axios.post(
        `${API_URL}/tenants/register`,
        registrationData
      );

      logger.info("✅ استجابة التسجيل:", response.data);

      if (response.data.success) {
        const { tenant, admin, token, requiresPayment } = response.data.data;
        const selectedPlan = plans.find((p) => p.id === formData.planId);

        // سجلات التشخيص
        logger.debug("🔍 تشخيص نوع الخطة:");
        logger.debug("  - Plan ID:", formData.planId);
        logger.debug("  - Selected Plan:", selectedPlan);
        logger.debug("  - Plan Price:", selectedPlan?.price);
        logger.debug("  - Token من Backend:", token ? "موجود ✅" : "غير موجود ❌");
        logger.debug("  - requiresPayment:", requiresPayment);

        // التحقق من نوع الخطة - استخدام requiresPayment من Backend كمصدر رئيسي
        if (requiresPayment === true) {
          // خطة مدفوعة - الانتقال لصفحة الدفع أو انتظار التفعيل
          logger.info("➡️ التوجيه: صفحة انتظار الدفع (خطة مدفوعة)");
          navigate("/payment-pending", {
            state: {
              tenant,
              admin,
              planId: formData.planId,
            },
          });
        } else if (token) {
          // خطة مجانية - تسجيل دخول تلقائي
          logger.info("➡️ التوجيه: لوحة التحكم (خطة مجانية)");
          // حفظ البيانات في localStorage
          localStorage.setItem("token", token);
          localStorage.setItem(
            "user",
            JSON.stringify({
              email: admin.email,
              name: admin.name,
              role: "admin",
              tenantId: tenant.id,
            })
          );
          localStorage.setItem("tenantId", tenant.id);
          localStorage.setItem("subdomain", tenant.subdomain);

          // الانتقال مباشرة للوحة التحكم
          navigate("/dashboard", {
            state: {
              isNewRegistration: true,
              tenant,
            },
          });
        } else {
          // حالة افتراضية - الانتقال لصفحة النجاح
          logger.info("➡️ التوجيه: صفحة النجاح (افتراضي)");
          navigate("/registration-success", {
            state: {
              tenant,
              admin,
            },
          });
        }
      }
    } catch (error) {
      logger.error("❌ خطأ في التسجيل:", error);
      logger.error("📋 تفاصيل الخطأ:", error.response?.data);
      const errorMessage =
        error.response?.data?.message || "حدث خطأ في التسجيل";
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // حساب نسبة التقدم
  const progress = ((currentStep + 1) / 3) * 100;

  const steps = [
    { title: "معلومات المكتب", icon: <Building2 className="w-5 h-5" /> },
    { title: "معلومات المدير", icon: <User className="w-5 h-5" /> },
    { title: "اختر الخطة", icon: <CreditCard className="w-5 h-5" /> },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-10 h-10 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              إنشاء حساب جديد
            </h1>
          </div>
          <p className="text-gray-600">
            املأ النموذج التالي لإنشاء حساب مكتبك على النظام
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="bg-gray-200 rounded-full h-2 mb-2">
            <div
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center text-sm text-gray-500">
            المرحلة {currentStep + 1} من 3
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div
                  className={`flex flex-col items-center flex-1 ${
                    index === currentStep ? "opacity-100" : "opacity-50"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                      index === currentStep
                        ? "bg-blue-600 text-white"
                        : index < currentStep
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {index < currentStep ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <span className="text-xs text-center hidden md:block">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 ${
                      index < currentStep ? "bg-green-500" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-red-800 text-sm">{errors.submit}</div>
            </div>
          )}

          {/* Step 1: معلومات المكتب */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                معلومات المكتب
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم المكتب <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.tenantName}
                    onChange={(e) => handleChange("tenantName", e.target.value)}
                    placeholder="مثال: مكتب المحاماة الذهبي"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.tenantName ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.tenantName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.tenantName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  النطاق الفرعي <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={formData.subdomain}
                      onChange={(e) =>
                        handleChange("subdomain", e.target.value)
                      }
                      placeholder="your-office"
                      dir="ltr"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${
                        errors.subdomain
                          ? "border-red-500"
                          : subdomainAvailable === false
                          ? "border-red-500"
                          : subdomainAvailable === true
                          ? "border-green-500"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                  <span className="text-gray-500 font-mono text-sm whitespace-nowrap">
                    .lawsystem.com
                  </span>
                </div>
                {checkingSubdomain && (
                  <p className="mt-1 text-sm text-gray-500">جاري التحقق...</p>
                )}
                {subdomainAvailable === true && (
                  <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" /> النطاق متاح
                  </p>
                )}
                {subdomainAvailable === false && (
                  <p className="mt-1 text-sm text-red-600">النطاق غير متاح</p>
                )}
                {errors.subdomain && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.subdomain}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  يمكنك استخدام الأحرف الإنجليزية الصغيرة، الأرقام، والشرطات فقط
                  (على الأقل 3 أحرف)
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  سيكون رابط مكتبك: {formData.subdomain || "your-office"}
                  .lawsystem.com
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      handleChange("contactEmail", e.target.value)
                    }
                    placeholder="contact@example.com"
                    dir="ltr"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${
                      errors.contactEmail ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.contactEmail && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.contactEmail}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) =>
                      handleChange("contactPhone", e.target.value)
                    }
                    placeholder="07901234567"
                    maxLength={15}
                    dir="ltr"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${
                      errors.contactPhone ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.contactPhone && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.contactPhone}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  مثال: 07901234567 أو +9647901234567
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute right-3 top-3 text-gray-400 w-5 h-5" />
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="العنوان الكامل للمكتب"
                    rows={3}
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.address ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: معلومات المدير */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                معلومات المدير
              </h2>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>معلومات مدير النظام</strong>
                    <p className="mt-1">
                      سيتم إنشاء حساب المدير الرئيسي بهذه المعلومات
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم المدير <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.adminName}
                    onChange={(e) => handleChange("adminName", e.target.value)}
                    placeholder="الاسم الكامل"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.adminName ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.adminName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.adminName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني للمدير{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => handleChange("adminEmail", e.target.value)}
                    placeholder="admin@example.com"
                    dir="ltr"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${
                      errors.adminEmail ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.adminEmail && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.adminEmail}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  كلمة المرور <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={formData.adminPassword}
                    onChange={(e) =>
                      handleChange("adminPassword", e.target.value)
                    }
                    placeholder="8 أحرف على الأقل"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.adminPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.adminPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.adminPassword}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  تأكيد كلمة المرور <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={formData.adminPasswordConfirm}
                    onChange={(e) =>
                      handleChange("adminPasswordConfirm", e.target.value)
                    }
                    placeholder="أعد إدخال كلمة المرور"
                    className={`w-full pr-11 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.adminPasswordConfirm
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>
                {errors.adminPasswordConfirm && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.adminPasswordConfirm}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  معايير كلمة المرور
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 8 أحرف على الأقل</li>
                  <li>• حرف كبير واحد على الأقل</li>
                  <li>• رقم واحد على الأقل</li>
                  <li>• رمز خاص واحد على الأقل (!@#$%)</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 3: اختر الخطة */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                اختر الخطة المناسبة
              </h2>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <strong>ابدأ بالخطة التجريبية مجاناً</strong>
                    <p className="mt-1">يمكنك الترقية لأي خطة أخرى في أي وقت</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {plans.map((plan, index) => (
                  <div
                    key={plan.id}
                    onClick={() => handleChange("planId", plan.id)}
                    className={`border rounded-lg p-6 flex flex-col bg-[#f8f9fa] transform hover:scale-105 transition-all duration-300 relative cursor-pointer ${
                      formData.planId === plan.id
                        ? "border-2 border-[#d4af37] shadow-2xl scale-105 bg-blue-50"
                        : plan.isPopular
                        ? "border-2 border-[#d4af37] shadow-2xl"
                        : "border-gray-200"
                    }`}
                  >
                    {plan.isPopular && (
                      <span className="bg-[#d4af37] text-[#1a3a64] text-xs font-bold px-3 py-1 rounded-full absolute -top-4 right-1/2 translate-x-1/2">
                        الأكثر شيوعًا
                      </span>
                    )}

                    {formData.planId === plan.id && (
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full absolute -top-4 right-1/2 translate-x-1/2">
                        الخطة المختارة
                      </span>
                    )}

                    <h3 className="text-2xl font-bold text-[#1a3a64] text-center">
                      {plan.name}
                    </h3>
                    <p className="text-gray-500 text-center mt-2 h-10">
                      {plan.description}
                    </p>
                    <div className="text-center my-6">
                      {plan.isContact ? (
                        <span className="text-4xl font-extrabold text-[#1a3a64]">
                          تواصل معنا
                        </span>
                      ) : (
                        <>
                          <span className="text-5xl font-extrabold text-[#1a3a64]">
                            {plan.price === 0
                              ? "٠"
                              : plan.price.toLocaleString("ar-IQ")}
                          </span>
                          <span className="text-gray-500">
                            {plan.price === 0
                              ? " د.ع / دائماً"
                              : ` د.ع / ${plan.period}`}
                          </span>
                        </>
                      )}
                    </div>
                    <ul className="space-y-4 mb-8 flex-grow">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <Check className="w-6 h-6 text-green-500 ml-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange("planId", plan.id);
                      }}
                      className={`w-full text-center font-bold py-3 px-6 rounded-lg transition ${
                        formData.planId === plan.id
                          ? "bg-blue-600 text-white"
                          : plan.isPopular
                          ? "bg-[#d4af37] text-[#1a3a64] hover:opacity-90"
                          : plan.isContact
                          ? "bg-gray-200 text-[#1a3a64] hover:bg-gray-300"
                          : "bg-[#1a3a64] text-white hover:bg-opacity-90"
                      }`}
                    >
                      {formData.planId === plan.id
                        ? "الخطة المختارة"
                        : plan.isContact
                        ? "اطلب عرضًا"
                        : plan.price === 0
                        ? "اختر الخطة المجانية"
                        : "اختر هذه الخطة"}
                    </button>
                  </div>
                ))}
              </div>

              {errors.planId && (
                <p className="mt-1 text-sm text-red-600">{errors.planId}</p>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            {currentStep > 0 ? (
              <button
                onClick={handlePrev}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                السابق
              </button>
            ) : (
              <div></div>
            )}

            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                التالي
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    جاري الإنشاء...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    إتمام التسجيل
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center mt-6 text-sm text-gray-500">
          لديك حساب بالفعل؟{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-blue-600 hover:underline font-semibold"
          >
            تسجيل الدخول
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterTenant;
